import React, { useState, useMemo } from 'react';

import { routes } from 'lane-shared/config';
import { SESSIONSTORAGE_ESSENSYS_RESERVABLE_VALUE } from 'lane-shared/helpers/integrations/Essensys/getCachedEssensysReservableValue';
import { useFlag } from 'lane-shared/hooks';
import { FeatureFlag } from 'constants-flags';
import { IntegrationProviderEnum } from 'constants-integrations';

import PastMeetings from 'components/integrations/ProxyClick/components/PastMeetings';
import { NavLinks } from 'components/general';
import { Loading } from 'components';
import ErrorMessage from 'components/general/ErrorMessage';
import { Button } from 'design-system-web';

import useScrollToTop from 'hooks/useScrollToTop';
import { ContentQueryInteraction } from 'lane-shared/hooks/useContentQuery';
import { useTranslation } from 'react-i18next';

import { isContentReservable } from './utils/searchParams';
import { useInteractionData } from './hooks/useInteractionData';
import { DEFAULT_FILTER_STATE } from './constants';
import { InteractionFilters } from './InteractionFilters';
import { NewUserContentInteractionReceipt } from 'components/lane/NewUserContentInteractionReceipt';
import UserContentInteractionReceipt from 'components/lane/UserContentInteractionReceipt';
import { FilterState } from './types';
import styles from './styles.scss';
import { ContentType } from 'lane-shared/types/content/Content';

type Props = {
  content: ContentType;
  interactions: ContentQueryInteraction[];
};

export default function ContentInteractions({
  content,
  interactions: initialInteractions,
}: Props) {
  const { t } = useTranslation();

  sessionStorage.removeItem(SESSIONSTORAGE_ESSENSYS_RESERVABLE_VALUE);
  const contentRoutes = [
    {
      name: content.name,
      route: routes.content.replace(':id', content._id),
    },
    {
      name: 'History',
      route: routes.contentInteractions.replace(':id', content._id),
    },
  ];

  useScrollToTop([content?._id]);

  const integrationProvider = content?.integration?.integration?.name;
  const showNewUserContentInteractionReceipt = useFlag(
    FeatureFlag.NewUserContentInteractionReceipt,
    false
  );
  const isReservableContent = useMemo(
    () => isContentReservable(content?.features),
    [content?.features]
  );

  const [filters, setFilters] = useState<FilterState>(DEFAULT_FILTER_STATE);

  const { interactions, loading, error, refetch } = useInteractionData({
    contentId: content._id,
    filters,
    isReservableContent,
    initialInteractions,
  });

  if (error) {
    return (
      <div className={styles.ContentInteractionsPage}>
        <NavLinks routes={contentRoutes} />
        <ErrorMessage error={error} />
        <div style={{ marginTop: '1em' }}>
          <Button size="medium" onClick={refetch}>
            {t(
              'shared.integrations.bePrism.BuildingIntegrationFailed.TryAgain'
            )}
          </Button>
        </div>
      </div>
    );
  }

  switch (integrationProvider) {
    case IntegrationProviderEnum.ProxyClick:
      return <PastMeetings interactions={interactions} />;
    default:
      return (
        <div className={styles.ContentInteractionsPage}>
          <NavLinks routes={contentRoutes} />
          <InteractionFilters
            isReservableContent={isReservableContent}
            filters={filters}
            onFiltersChange={setFilters}
          />
          {showNewUserContentInteractionReceipt ? (
            <div className={styles.newInteractions}>
              {interactions.map(interaction => (
                <NewUserContentInteractionReceipt
                  key={interaction?._id}
                  // FIXME: Fix the types here
                  // @ts-ignore
                  interaction={interaction}
                />
              ))}
            </div>
          ) : (
            <div className={styles.interactions}>
              {interactions.map(interaction => (
                <div
                  key={interaction._id}
                  className={styles.interactionContainer}
                >
                  <UserContentInteractionReceipt
                    className={styles.interaction}
                    // @ts-expect-error ts-migrate(2741) FIXME: Property '_createdBy' is missing in type 'ContentQ... Remove this comment to see the full error message
                    interaction={interaction}
                  />
                </div>
              ))}
            </div>
          )}
          {loading && <Loading />}
        </div>
      );
  }
}
